import axios from 'shared/util/axios';
import env from 'builder/config';

const version = env.permissionsEnabled ? 'v4' : 'v2';

const fetchSites = () => {
  return axios
    .get<Array<Site>>(`site-builder/${version}/siteVersions`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchSitesPublishedMessages = (siteId: number) => {
  return axios
    .get<Array<SitePublishedMessages>>(
      `site-builder/${version}/siteVersions/publishedMessages/${siteId}`
    )
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchAllSiteVersions = (siteId: number) => {
  return axios
    .get<Array<Site>>(`site-builder/${version}/siteVersions/${siteId}`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchSitesByUuid = () => {
  return axios
    .get<Array<Site>>(`site-builder/${version}/siteVersions/sitesByUuid`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchSite = (siteId: number) => {
  return axios
    .get(`site-builder/${version}/siteVersions/${siteId}/current`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const createSite = (site: Partial<Site>) => {
  return axios
    .post<Site>(`site-builder/${version}/siteVersions`, site)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const saveSite = (siteId: number, site: Site) => {
  return axios
    .put<Site>(`site-builder/${version}/siteVersions/${siteId}`, site, {
      responseType: 'json',
    })
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const publishSite = (siteId: number, publishMessage: string) => {
  return axios
    .put(
      `site-builder/${version}/siteVersions/${siteId}/publish?publishedMessage=${publishMessage}`
    )
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const publishSiteToDemo = (siteId: number) => {
  return axios
    .put(`site-builder/${version}/siteVersions/${siteId}/publish?isDemo=true`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const unPublishSite = (siteId: number) => {
  return axios
    .put(`site-builder/${version}/siteVersions/${siteId}/unpublish`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const archiveSite = (siteId: number) => {
  return axios
    .put(`site-builder/${version}/siteVersions/${siteId}/archive`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchPages = (siteId: number) => {
  return axios
    .get<Array<Page>>(`site-builder/v4/siteVersion/${siteId}/pages`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchPage = (siteId: number, pageId: number) => {
  return axios
    .get<Page[]>(`site-builder/v4/siteVersion/${siteId}/pages/${pageId}`)
    .then(response => response.data[0])
    .catch(error => {
      return Promise.reject(error);
    });
};

const createPage = (siteId: number, page: Partial<PageVersion>) => {
  return axios
    .post<Page>(`site-builder/v4/siteVersion/${siteId}/pages`, page)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const savePage = (siteId: number, pageId: number, page: PageVersion) => {
  return axios
    .put<PageVersion>(
      `site-builder/v4/siteVersion/${siteId}/pages/${pageId}`,
      page
    )
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const saveAllPage = async (siteId: number, pages: Array<PageVersion>) => {
  try {
    const response = await axios.put<Array<PageVersion>>(
      `site-builder/v3/sites/${siteId}/pages`,
      pages
    );

    return response.data;
  } catch (error) {
    return await Promise.reject(error);
  }
};

const createFormWidget = (
  siteId: number,
  pageId: number,
  widget: FormWidget
) => {
  return axios
    .post<FormWidget>(
      `site-builder/v3/sites/${siteId}/pages/${pageId}/widgets/form`,
      widget
    )
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const updateFormWidget = (
  siteId: number,
  pageId: number,
  widget: FormWidget
) => {
  return axios
    .put<FormWidget>(
      `site-builder/v3/sites/${siteId}/pages/${pageId}/widgets/form`,
      widget
    )
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const removePage = (siteId: number, pageId: number) => {
  return axios
    .delete(`site-builder/v4/siteVersion/${siteId}/pages/${pageId}`)
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const getPagesByTime = (siteId: number, deployedTime: number) => {
  return axios
    .get<Array<Page>>(
      `site-builder/v4/siteVersion/${siteId}/pages/pagesByTime/${deployedTime}`
    )
    .then(response => response.data)
    .catch(error => {
      return Promise.reject(error);
    });
};

const fetchSearchResults = (query: string, site: Site) => {
  // Sanitize and normalize the query to prevent unsafe characters from reaching the API
  const sanitizedQuery =
    typeof query === 'string' ? query.replace(/[<>"'%;()&+]/g, '').trim() : '';

  const payload = {
    query: sanitizedQuery,
    searchServices: ['atlas'],
    metadata: {
      aideId: site.aideId,
      siteId: site.siteId,
      siteName: site.name,
    },
  };

  return axios
    .post('horizon-search-api/api/v1/search', payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then(response => {
      if (!response.data || !Array.isArray(response.data.responses)) {
        return [];
      }
      return response.data.responses.map((item: any) => {
        // Support both response shapes:
        //   v1 (flat):   item.pageTitle, item.pageFolder, item.pageUrl
        //   v2 (nested): item.metadata.pageTitle, item.metadata.pageFolder,
        //                item.metadata.siteDomainURL + item.metadata.pageSlug
        const meta = item.metadata ?? {};

        // --- Title & category ---
        const title: string = meta.pageTitle ?? item.pageTitle ?? '';
        const category: string =
          meta.pageFolder ?? item.pageFolder ?? 'Uncategorized';

        // --- Description ---
        // Content format v1: "<timestamp> - content: <body text>"
        // Content format v2: "<timestamp> - <body text>"
        const contentStr: string = item.content ?? '';
        let description = '';
        if (contentStr.includes(' - content: ')) {
          description = contentStr.split(' - content: ')[1].trim();
        } else {
          const dashIndex = contentStr.indexOf(' - ');
          description =
            dashIndex !== -1 ? contentStr.slice(dashIndex + 3).trim() : '';
        }

        // --- Link ---
        // v1 provides pageUrl directly; v2 requires construction from
        // siteDomainURL + pageSlug (e.g. "example.com" + "page2" → "https://example.com/page2").
        let rawUrl: string = item.pageUrl ?? '';
        if (!rawUrl && meta.siteDomainURL) {
          const slug: string = meta.pageSlug ? `/${meta.pageSlug}` : '';
          rawUrl = `${meta.siteDomainURL}${slug}`;
        }
        const link = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`;

        // --- Date ---
        // v2 supplies an ISO date via metadata.publishedAt; v1 embeds a date
        // at the start of the content string (e.g. "2026-03-04T13:11:21+0000 - ...").
        let extractedDate = '1970-01-01';
        if (meta.publishedAt) {
          const metaDate = String(meta.publishedAt).match(
            /^(\d{4}-\d{2}-\d{2})/
          );
          if (metaDate) extractedDate = metaDate[1];
        } else {
          const contentDate = contentStr.match(/^(\d{4}-\d{2}-\d{2})/);
          if (contentDate) extractedDate = contentDate[1];
        }

        return {
          category,
          title,
          description,
          link,
          dateUpdated: extractedDate,
        };
      });
    })
    .catch(error => {
      console.error(
        'Error fetching search results:',
        query,
        site,
        error.response?.data || error.message
      );
      return [];
    });
};

export {
  fetchSites,
  fetchAllSiteVersions,
  createSite,
  saveSite,
  publishSite,
  publishSiteToDemo,
  unPublishSite,
  fetchPages,
  fetchPage,
  createPage,
  savePage,
  saveAllPage,
  createFormWidget,
  updateFormWidget,
  removePage,
  archiveSite,
  fetchSite,
  fetchSitesByUuid,
  fetchSearchResults,
  fetchSitesPublishedMessages,
  getPagesByTime,
};
